import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { avatarTone, initialsOf } from '../../../common/libs/initials';
import { IconChevron, IconEdit } from '../../../common/icons';
import DeactivateButton from '../../../common/components/deactivate/deactivate-button';
import { userKeys } from '../queries/keys';
import { userPaths } from '../queries/paths';
import { USERS_PAGE_SIZE, useUserTeams, useUsersList } from '../contexts/users';
import { ROLE_OPTIONS } from '../utils/form';
import SearchBox from '../../../common/components/search-box/search-box';

import '../styles/overview.css';

// A list and not a grid of cards: a user is a row of facts — name, mail, role,
// state — and rows put those facts in columns that line up down the page. The
// same reason clients ARE cards: there the interesting part is each one on its
// own, not the comparison between them.
//
// Each row opens. What it opens to is the one thing the row cannot fit and
// somebody always wants next: WHICH TEAMS this person is on.
//
// That used to be inverted from the teams side — every team, then every team's
// members — because no route answered it, and the result could be quietly
// incomplete. AD01 published `GET /admin/users/{id}/teams`, so now each row
// asks for its own, once, when it opens.

const STATUS_LABEL = {
    2: 'Activo',
    1: 'Inactivo',
    0: 'Eliminado',
};

const ROLE_LABEL = Object.fromEntries(
    ROLE_OPTIONS.map(option => [option.value, option.label]),
);

const DATE_FORMAT = new Intl.DateTimeFormat('es', {
    day: 'numeric', month: 'short', year: 'numeric',
});

// The list answers rows from the model (createdAt) while the detail answers an
// assembled package (created_at). Both spellings are read because the
// published contract documents neither response.
function readDate(raw) {
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(raw) {
    const date = readDate(raw);
    return date ? DATE_FORMAT.format(date) : null;
}

function joinedAt(user) {
    return readDate(user.created_at ?? user.createdAt);
}

// FILTRO Y ORDEN CORREN ACÁ, y no es una elección de diseño: `GET /admin/users`
// acepta `limit`, `offset` y `q`, y nada más. No hay parámetro de estado ni de
// orden — la ruta ordena por `id DESC` y punto.
//
// So both act on the page that was loaded, never on the table. The screen says
// that out loud rather than letting a filtered count read as a total. Recorded
// as a backend pending; the honest fix is `?status=` and an order parameter.
const STATUS_FILTERS = [
    { value: '',  label: 'Todos' },
    { value: '2', label: 'Activos' },
    { value: '1', label: 'Inactivos' },
];

const SORTS = [
    { value: '',         label: 'Los últimos cargados' },
    { value: 'joined',   label: 'Ingreso: más reciente' },
    { value: 'joined-',  label: 'Ingreso: más antiguo' },
];

function UserRow({ user }) {
    const [open, setOpen] = useState(false);

    // Cada fila pide LO SUYO, y recién al abrirse.
    //
    // Before AD01 this was inverted from the teams side — one request for the
    // teams plus one per team — and shared by every row, because doing it per
    // person would have meant that scan forty times. Now a membership list is
    // one request for one person, so it belongs to the row that asked for it.
    const teams = useUserTeams(user.id, { enabled: open });

    const lastLogin = formatDate(user.last_login_at ?? user.lastLoginAt);
    const joined = formatDate(user.created_at ?? user.createdAt);
    const panelId = `user-${user.id}-teams`;

    return (
        <li className={`user-row-shell ${open ? 'user-row-shell--open' : ''}`}>
            <div className="user-row">
                {/* The whole row is not the control: the edit link lives inside
                    it, and nesting a link in a button is not something a
                    keyboard or a screen reader can take apart. The arrow is the
                    control, and it is labelled. */}
                <button
                    type="button"
                    className="user-row__toggle"
                    onClick={() => setOpen(value => !value)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    aria-label={`${open ? 'Ocultar' : 'Ver'} los equipos de ${user.name}`}
                >
                    <span className={`user-row__chevron ${open ? 'user-row__chevron--open' : ''}`}>
                        <IconChevron />
                    </span>
                </button>

                <span
                    className={`avatar user-row__avatar avatar--tone-${avatarTone(user.name || user.email)}`}
                    aria-hidden="true"
                >
                    {initialsOf(user.name || user.email)}
                </span>

                <span className="user-row__identity">
                    <span className="user-row__name">{user.name}</span>
                    <span className="user-row__email">{user.email}</span>
                </span>

                <span className="user-row__role">
                    {ROLE_LABEL[user.role] ?? user.role}
                </span>

                <span className={`user-row__status user-row__status--${user.status}`}>
                    {STATUS_LABEL[user.status] ?? 'Desconocido'}
                </span>

                <span className="user-row__seen">
                    {lastLogin ? `Entró el ${lastLogin}` : 'Nunca entró'}
                </span>

                <span className="row-actions">
                    <Gate domain="users:write">
                        <Link
                            to={`/admin/users/${user.id}`}
                            className="user-row__edit"
                            aria-label={`Editar ${user.name}`}
                            title="Editar"
                        >
                            <IconEdit />
                        </Link>
                    </Gate>
                    <Gate domain="users:admin">
                        <DeactivateButton
                            name={user.name}
                            noun="usuario"
                            path={userPaths.detail(user.id)}
                            invalidate={[userKeys.all]}
                            consequence="Queda inactivo y ya no puede entrar al panel. La fila se conserva, con toda su historia: no se borra nada. Se puede volver a activar editándolo, en «Estado del registro»."
                        />
                    </Gate>
                </span>
            </div>

            {open && (
                <div className="user-row__panel" id={panelId}>
                    <dl className="user-row__facts">
                        <div>
                            <dt>Alta en el sistema</dt>
                            <dd>{joined || '—'}</dd>
                        </div>
                        <div>
                            <dt>
                                Equipos
                                {teams.total > 0 && (
                                    <span className="user-row__facts-count"> · {teams.total}</span>
                                )}
                            </dt>
                            <dd>
                                {/* The route demands `users:read` AND `teams:read`.
                                    Said rather than fired: a 403 from a row
                                    somebody opened trips the global permission
                                    toast and tells them they did something
                                    wrong when they only looked. */}
                                {!teams.allowed
                                    ? <span className="user-row__muted">Hace falta permiso de equipos para verlos.</span>
                                    : teams.isLoading
                                        ? <span className="user-row__muted">Buscando...</span>
                                        : teams.error
                                            ? <span className="user-row__muted">{teams.error.message || 'No se pudieron leer sus equipos.'}</span>
                                            : teams.items.length === 0
                                                ? <span className="user-row__muted">No está en ningún equipo activo.</span>
                                                : (
                                                    <ul className="user-row__teams">
                                                        {teams.items.map(entry => (
                                                            <li key={entry.membership_id}>
                                                                <Link to={`/admin/teams/${entry.team_id}`}>
                                                                    {entry.team_name ?? `Equipo #${entry.team_id}`}
                                                                </Link>
                                                                {/* `lead` is the only one that grants
                                                                    anything; the rest describe. Marked
                                                                    so the difference is visible. */}
                                                                <span className={`user-row__team-role ${entry.team_role === 'lead' ? 'user-row__team-role--lead' : ''}`}>
                                                                    {entry.team_role}
                                                                </span>
                                                                {/* «Primer ingreso», y el título lo
                                                                    aclara: una reactivación conserva
                                                                    la fecha, así que NO es desde
                                                                    cuándo está esta vez. */}
                                                                {entry.joined_at && (
                                                                    <span
                                                                        className="user-row__team-since"
                                                                        title="Primer ingreso registrado. Si salió y volvió, la fecha es la de la primera vez."
                                                                    >
                                                                        desde {formatDate(entry.joined_at)}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                            </dd>
                        </div>
                    </dl>

                    {teams.allowed && teams.partial && (
                        <p className="user-row__muted">
                            Se muestran {teams.items.length} de {teams.total} equipos.
                        </p>
                    )}

                    {teams.allowed && teams.items.length > 0 && (
                        <p className="user-row__muted">
                            Solo equipos activos: uno dado de baja no aparece acá aunque la
                            persona siga en su registro.
                        </p>
                    )}
                </div>
            )}
        </li>
    );
}

export default function UsersOverview() {
    const [params, setParams] = useSearchParams();

    const page = Math.max(Number(params.get('page')) || 1, 1);
    const q = params.get('q') || '';
    const status = params.get('status') || '';
    const sort = params.get('sort') || '';
    const offset = (page - 1) * USERS_PAGE_SIZE;

    const { data, isLoading, isFetching, error } = useUsersList({
        limit: USERS_PAGE_SIZE,
        offset,
        q,
    });

    const loaded = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? 0;

    const items = useMemo(() => {
        let rows = loaded;
        if (status) rows = rows.filter(user => String(user.status) === status);

        if (sort) {
            const direction = sort === 'joined-' ? 1 : -1;
            rows = [...rows].sort((a, b) => {
                const left = joinedAt(a)?.getTime() ?? 0;
                const right = joinedAt(b)?.getTime() ?? 0;
                // Tie broken by id so two people created the same second do not
                // swap places between renders.
                return left === right ? (a.id - b.id) * direction : (left - right) * direction;
            });
        }

        return rows;
    }, [loaded, status, sort]);

    const lastPage = useMemo(
        () => Math.max(Math.ceil(total / USERS_PAGE_SIZE), 1),
        [total],
    );

    const setParam = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        target.delete('page');
        setParams(target);
    };

    const goToPage = (next) => {
        const target = new URLSearchParams(params);
        if (next <= 1) target.delete('page');
        else target.set('page', String(next));
        setParams(target);
    };

    if (isLoading && !data) {
        return <p className="users__loading">Cargando usuarios...</p>;
    }

    if (error) {
        return (
            <p className="users__error">
                {error.message || 'No se pudieron cargar los usuarios'}
            </p>
        );
    }

    const filtered = items.length !== loaded.length;

    return (
        <section className="users">
            <header className="users__header">
                <div className="users__heading">
                    <span className="users__count">
                        {total} {total === 1 ? 'persona' : 'personas'}
                    </span>
                    <h2 className="users__title">Usuarios</h2>
                </div>

                <Gate domain="users:write">
                    <Link to="/admin/users/new" className="users__new">
                        + Nueva persona
                    </Link>
                </Gate>
            </header>

            <SearchBox placeholder="Buscar por nombre o correo" />

            <div className="users__filters">
                <label className="users__filter">
                    <span className="users__filter-label">Estado</span>
                    <select
                        className="users__filter-input"
                        value={status}
                        onChange={e => setParam('status', e.target.value)}
                    >
                        {STATUS_FILTERS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>

                <label className="users__filter">
                    <span className="users__filter-label">Orden</span>
                    <select
                        className="users__filter-input"
                        value={sort}
                        onChange={e => setParam('sort', e.target.value)}
                    >
                        {SORTS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
            </div>

            {/* SAID OUT LOUD, because the alternative is a count that reads as a
                total. The list endpoint takes neither a status filter nor an
                order, so both controls above act on the page that was loaded —
                filtering to «Activos» does not go looking for the active people
                on page three. */}
            {(status || sort) && total > loaded.length && (
                <p className="users__scope-note">
                    El estado y el orden se aplican sobre <strong>esta página</strong> de{' '}
                    {loaded.length}, no sobre las {total} personas: el listado del backend no
                    acepta filtro de estado ni orden.
                </p>
            )}

            {!items.length && (
                <p className="users__empty">
                    {filtered
                        ? 'Ninguna persona de esta página coincide con el filtro.'
                        : q
                            ? `Ninguna persona coincide con "${q}".`
                            : 'Todavía no hay usuarios cargados.'}
                </p>
            )}

            {!!items.length && (
                <div className={`users__list ${isFetching ? 'users__list--refreshing' : ''}`}>
                    {/* Column headings, hidden on narrow where the row stops
                        being a row. aria-hidden because they label nothing a
                        screen reader can pair them with: each cell already
                        carries its own text. */}
                    <div className="user-row user-row--head" aria-hidden="true">
                        <span />
                        <span />
                        <span>Persona</span>
                        <span>Rol</span>
                        <span>Estado</span>
                        <span>Último ingreso</span>
                        <span />
                    </div>

                    <ul className="users__rows">
                        {items.map(user => <UserRow key={user.id} user={user} />)}
                    </ul>
                </div>
            )}

            {total > USERS_PAGE_SIZE && (
                <nav className="users__pager">
                    <button
                        type="button"
                        className="users__pager-button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                    >
                        Anterior
                    </button>

                    <span className="users__pager-position">
                        Página {page} de {lastPage}
                    </span>

                    <button
                        type="button"
                        className="users__pager-button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= lastPage}
                    >
                        Siguiente
                    </button>
                </nav>
            )}
        </section>
    );
}
